import { findingSourceTypesEnum, FindingType_Enumerated, FindingType_Numeric } from '@app/advisor-shared';
import { cloneObj, exists, removeItemFromArray, StaticLogger } from '@nu-art/ts-common';
import { ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
const checkValueMatch = (suggestedFinding, prf) => {
    switch (suggestedFinding.type) {
        case 'enumerated':
            return suggestedFinding.valueIds?.[0] === prf.valueIds?.[0];
        case 'numeric':
            return suggestedFinding.value === prf.value;
    }
};
const getInteractableSuggestedFinding = (provideInfoFindings, variableId) => {
    const toCreate = provideInfoFindings.toCreate.find(item => item.variableId === variableId);
    if (toCreate?.suggestedFinding && !toCreate.suggestedFinding.explicitReject && !toCreate.suggestedFinding.userInteracted)
        return toCreate.suggestedFinding;
};
export const resolve_ProvideInfoFindings_EMFindings = (provideInfoFindings) => {
    const emFindings = {
        processedRawFindings: provideInfoFindings.toCreate.map(item => cloneObj(item)),
        findingsToUpdate: provideInfoFindings.toUpdate.map(item => cloneObj(item)),
    };
    //Filter #1 - filter out unchanged findings
    emFindings.findingsToUpdate = emFindings.findingsToUpdate.filter(ftu => {
        const original = ModuleFE_RuntimeFinding.cache.unique(ftu.findingId);
        if (!original)
            return true;
        if (original.type === FindingType_Enumerated) {
            const toUpdate = ftu;
            if (original.valueIds?.[0] !== toUpdate.valueIds?.[0])
                return true;
            for (const att of toUpdate.attributes ?? []) {
                const existing = original.attributes?.find(i => i.variableId === att.variableId);
                if (!existing || existing.valueIds?.[0] !== att.valueIds?.[0])
                    return true;
            }
        }
        else if (original.type === FindingType_Numeric) {
            return original.value !== ftu.value;
        }
        return false;
    });
    emFindings.processedRawFindings = emFindings.processedRawFindings.filter(prf => prf.source === findingSourceTypesEnum.ADVISOR || getInteractableSuggestedFinding(provideInfoFindings, prf.variableId));
    //Filter #2 - filter out findings that are partial calculations
    emFindings.findingsToUpdate = emFindings.findingsToUpdate.filter(ftu => !ftu.isPartialCalculation);
    emFindings.processedRawFindings = emFindings.processedRawFindings.filter(prf => !prf.isPartialCalculation);
    //Filter #3 - filter out findings with no values
    emFindings.findingsToUpdate = emFindings.findingsToUpdate.filter(ftu => ftu.type === 'enumerated' ? exists(ftu.valueIds?.[0]) : exists(ftu.value));
    emFindings.processedRawFindings = emFindings.processedRawFindings.filter(prf => {
        const suggestedFinding = getInteractableSuggestedFinding(provideInfoFindings, prf.variableId);
        //If the toCreate finding has a suggested finding, and the field has not been rejected or interacted with, we inject the selected value and approve the finding
        if (suggestedFinding && (!suggestedFinding.explicitReject && !suggestedFinding.userInteracted)) {
            const suggestedValue = (!suggestedFinding || suggestedFinding.userInteracted || suggestedFinding.explicitReject)
                ? undefined
                : (suggestedFinding.finding.type === 'enumerated' ? suggestedFinding.finding.valueIds : suggestedFinding.finding.value);
            if (prf.type === 'enumerated')
                prf.valueIds = suggestedValue;
            else
                prf.value = suggestedValue;
            return true;
        }
        //We don't filter by suggested finding, filter by existence of actual value
        return prf.type === 'enumerated' ? exists(prf.valueIds?.[0]) : exists(prf.value);
    });
    //Filter #4 - Filter out attributes with no values, clean sourcing
    emFindings.findingsToUpdate.forEach(ftu => {
        //Clean sourcing
        delete ftu.source;
        if (ftu.type !== 'enumerated' || !ftu.attributes?.length)
            return;
        ftu.attributes = ftu.attributes.filter(att => !!att.valueIds?.length);
        ftu.attributes.forEach(att => {
            if ('source' in att) //Clean sourcing
                delete att.source;
        });
    });
    emFindings.processedRawFindings.forEach(prf => {
        //Clean sourcing
        delete prf.source;
        if (prf.type !== 'enumerated' || !prf.attributes?.length)
            return;
        prf.attributes = prf.attributes.filter(att => !!att.valueIds?.length);
        prf.attributes.forEach(att => {
            if ('source' in att) //Clean sourcing
                delete att.source;
        });
    });
    //Return em resolved findings
    return emFindings;
};
/**
 * This function resolves each suggested finding that was attached to a "findingToCreate" into "rejected" or "accepted".
 * Only use this function when about to update encounter
 * @param provideInfoFindings
 * @param emFindings
 */
export const resolve_EMSuggestedFindings = (provideInfoFindings, emFindings) => {
    const acceptedIds = [];
    const rejectedIds = [];
    provideInfoFindings.toCreate.forEach(toCreate => {
        //Do not resolve non-viewed suggested findings
        if (!toCreate.suggestedFinding || !toCreate.suggestedFinding.viewed)
            return;
        const suggestedFindingId = toCreate.suggestedFinding.finding._id;
        if (toCreate.suggestedFinding.explicitReject) //User explicitly rejected
            return rejectedIds.push(suggestedFindingId);
        //Get the resolved EMFinding
        const resolvedEMFinding = emFindings.processedRawFindings.find(item => item.variableId === toCreate.variableId);
        //Resolved EM Findings did not pass filtration
        if (!resolvedEMFinding) {
            //Did not pass filtration due to user selection
            if (toCreate.suggestedFinding.userInteracted)
                return rejectedIds.push(suggestedFindingId);
            else
                return StaticLogger.logError(`Could not match resolved EM finding for variable id ${toCreate.variableId}, even though no user interaction was recorded for variable`);
        }
        //Resolved EM Finding passed filtration
        if (!toCreate.suggestedFinding.userInteracted) { //User did not interact with variable, we implicitly accept
            //Remove the resolved finding from EM findings, as to not "double create" it
            removeItemFromArray(emFindings.processedRawFindings, resolvedEMFinding);
            return acceptedIds.push(suggestedFindingId);
        }
        //User interacted with the variable, infer suggestion by checking if actual value matches suggested value
        const valueMatch = checkValueMatch(toCreate.suggestedFinding.finding, resolvedEMFinding);
        if (valueMatch) {
            acceptedIds.push(suggestedFindingId);
            //Remove the resolved finding from EM findings, as to not "double create" it
            removeItemFromArray(emFindings.processedRawFindings, resolvedEMFinding);
        }
        else {
            rejectedIds.push(suggestedFindingId);
        }
    });
    return { acceptedIds, rejectedIds };
};
//# sourceMappingURL=em-resolving.js.map